import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { deleteAuthCookie } from '@clatter/platform';
import { isUnauthorizedError, isAbortError } from '../helpers/apiErrors';
import { checkSession } from '../store';
import { deleteLocalStorageAuth } from '../helpers/auth';

// simple mutex implementation
let refreshMutex: Promise<any> | null = null;
let isLoggingOut = false;

const performLogout = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  deleteLocalStorageAuth();
  deleteAuthCookie();

  //this is to cancel any pending rtk requests
  window.location.href = '/logout';
};

export const createBaseQueryWithReauth = (
  rawBaseQuery: ReturnType<typeof fetchBaseQuery>,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  return async (args, api, extraOptions) => {
    // wait for any ongoing refresh to complete
    if (refreshMutex) {
      await refreshMutex;
    }

    // if logout started while waiting, abort this request
    if (isLoggingOut) {
      return { data: undefined };
    }

    let result = await rawBaseQuery(args, api, extraOptions);

    // suppress abort errors globally
    if (result.error && isAbortError(result.error)) {
      return { data: undefined };
    }

    if (isUnauthorizedError(result.error) && !isLoggingOut) {
      // if no refresh is in progress, start one
      if (!refreshMutex) {
        refreshMutex = (async () => {
          try {
            await (api.dispatch(checkSession()) as any).unwrap();
            return true;
          } catch (error) {
            return false;
          } finally {
            refreshMutex = null;
          }
        })();
      }

      // wait for refresh to complete
      const refreshSucceeded = await refreshMutex;

      if (!refreshSucceeded) {
        performLogout();
        return { data: undefined }; // suppress error notification
      }

      // refresh succeeded, retry the request
      result = await rawBaseQuery(args, api, extraOptions);

      // any error after refresh = logout
      // this is cruical to break infinite loop
      // if we use only isUnauthorizedError(result.error)
      // it does not work...
      if (result.error) {
        performLogout();
        return { data: undefined }; // suppress error notification
      }
    }

    return result;
  };
};
